import { useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Table, Pagination, Loader, MobileTable } from '../../../../components';
import { Button, Box, useMediaQuery } from '@mui/material';
import { Download } from '@mui/icons-material';
import { getDocuments, downloadPandaDoc } from '../../../../fetch';
import {
  formatDate,
  downloadFile,
  getDownloadFileName,
  hyphenSeparateTwoInputs
} from '../../../../helpers';
import { IDocument } from '../../../../models';
import { useMutation, useQuery } from 'react-query';

export const ExecutedContracts = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedSort, setSelectedSort] = useState<string>('Name');
  const [sortDirection, setSortDirection] = useState<{
    documentName?: 'Asc' | 'Desc';
    personType?: 'Asc' | 'Desc';
    partnerSubType?: 'Asc' | 'Desc';
    partnerName?: 'Asc' | 'Desc';
    personFirstName?: 'Asc' | 'Desc';
    personLastName?: 'Asc' | 'Desc';
    status?: 'Asc' | 'Desc';
    createdDate?: 'Asc' | 'Desc';
    updatedDate?: 'Asc' | 'Desc';
  }>({
    documentName: 'Asc'
  });

  const isMobile = useMediaQuery('(max-width: 960px)');

  const { data, isLoading } = useQuery(
    ['executedContracts', page, perPage, selectedSort, sortDirection],
    () =>
      getDocuments({
        page: page + 1,
        perPage: perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        statuses: ['Completed']
      }),
    {
      onSuccess: res => {
        setRecordCount(res.totalRecordCount);
      }
    }
  );

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const { mutate: handleDownload, isLoading: isDownLoading } = useMutation(
    (id: string) => downloadPandaDoc(id),
    {
      onSuccess: res => {
        const fileName = getDownloadFileName(res);
        downloadFile(res.data, fileName);
      },
      onError: () => {
        enqueueSnackbar(`Error, unable to download file, please try again.`, {
          variant: 'error'
        });
      }
    }
  );

  const columns = useMemo(() => {
    return [
      {
        Header: 'Date Created',
        accessor: 'createdDate',
        isServerSorted: selectedSort === 'createdDate',
        isServerSortedDesc: sortDirection.createdDate === 'Desc',
        handleClickColumn: () => handleClickColumn('createdDate'),
        Cell: props => <>{formatDate(props.cell.row.original.createdDate)}</>
      },
      {
        Header: 'Name',
        accessor: 'documentName',
        isServerSorted: selectedSort === 'documentName',
        isServerSortedDesc: sortDirection.documentName === 'Desc',
        handleClickColumn: () => handleClickColumn('documentName')
      },
      {
        Header: 'Type',
        accessor: 'person.type',
        isServerSorted: selectedSort === 'personType',
        isServerSortedDesc: sortDirection.personType === 'Desc',
        handleClickColumn: () => handleClickColumn('personType')
      },
      {
        Header: 'Sub-Type',
        accessor: 'partner.type',
        isServerSorted: selectedSort === 'partnerSubType',
        isServerSortedDesc: sortDirection.partnerSubType === 'Desc',
        handleClickColumn: () => handleClickColumn('partnerSubType')
      },
      {
        Header: 'Company Name',
        accessor: 'partner.name',
        isServerSorted: selectedSort === 'partnerName',
        isServerSortedDesc: sortDirection.partnerName === 'Desc',
        handleClickColumn: () => handleClickColumn('partnerName')
      },
      {
        Header: 'Contact Name',
        accessor: 'person.firstName',
        sort: false,
        Cell: props => (
          <>
            {props.cell.row.original?.person?.firstName} {props.cell.row.original?.person?.lastName}
          </>
        )
      },
      {
        Header: 'Date Updated',
        accessor: 'updatedDate',
        isServerSorted: selectedSort === 'updatedDate',
        isServerSortedDesc: sortDirection.updatedDate === 'Desc',
        handleClickColumn: () => handleClickColumn('updatedDate'),
        Cell: props => <>{formatDate(props.cell.row.original.updatedDate)}</>
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocument } };
        }) => {
          return (
            <Box>
              <Button
                id="edit"
                color="primary"
                startIcon={<Download />}
                onClick={() => handleDownload(original.documentId)}
              >
                Download
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.records]);
  return (
    <>
      {isDownLoading && <Loader type="overlay" position="centered" />}
      <Table
        columns={columns}
        data={data?.records ?? []}
        isLoading={isLoading}
        hidePagination
        mobileProps={{
          getAccordionHeader: (item: IDocument) => {
            return hyphenSeparateTwoInputs(formatDate(item.createdDate), item.documentName);
          },
          fields: columns
            .filter(c => c.Header)
            .map(c => ({
              name: c.Header,
              accessor: c.accessor,
              Cell: c?.Cell
            })),
          handleDownload: (val: IDocument) => {
            handleDownload(val.documentId);
          }
        }}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
      />
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </>
  );
};
